import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

const faqData = [
    {
        category: "General",
        items: [
            {
                question: "1. What is InvexFlow?",
                answer:
                    "InvexFlow is a digital invoice discounting platform that enables hospitals and automobile workshops to access immediate working capital against their pending insurance claim invoices. By connecting businesses, insurance companies, and financial institutions, InvexFlow transforms receivables into instant liquidity.",
            },
            {
                question: "2. How does InvexFlow work?",
                answer:
                    "InvexFlow facilitates a structured process: Businesses upload insurance-backed invoices, insurance companies validate and approve claims, partner banks finance the approved invoices, and insurance companies settle payments directly with the bank on maturity.",
            },
            {
                question: "3. Who can use InvexFlow?",
                answer:
                    "Currently, InvexFlow is designed for hospitals, automobile workshops, and other service providers dealing with insurance-based receivables.",
            },
            {
                question: "4. What types of invoices are eligible?",
                answer:
                    "Invoices must be linked to insurance claims, verifiable and approved by an insurance company, and meet documentation and compliance requirements.",
            },
        ],
    },

    {
        category: "For Businesses (Hospitals & Workshops)",
        items: [
            {
                question: "5. How quickly can I receive funds?",
                answer:
                    "Once the invoice is verified and approved, funding is typically processed within a short turnaround time depending on partner bank evaluation and documentation completion.",
            },
            {
                question: "6. How much financing can I get?",
                answer:
                    "Businesses can typically access up to 70–80% of the approved invoice value, subject to bank policies and risk assessment.",
            },
            {
                question: "7. Do I need collateral?",
                answer:
                    "No traditional collateral is required. Financing is primarily based on the strength of the approved invoice and the credibility of the insurance company.",
            },
            {
                question: "8. What are the fees involved?",
                answer:
                    "Fees may include a processing fee and a discounting fee based on invoice tenure and risk. All charges are transparently communicated before funding.",
            },
            {
                question: "9. Will my customer (insurance company) be informed?",
                answer:
                    "Yes. Invoice validation and approval involve coordination with the insurance company to ensure authenticity and secure settlement.",
            },
            {
                question: "10. What happens after I receive funding?",
                answer:
                    "On the due date, the insurance company pays the financing bank directly, applicable fees are deducted, and any remaining balance is transferred to your account.",
            },
        ],
    },

    {
        category: "For Insurance Companies",
        items: [
            {
                question: "11. What is the role of insurance companies in InvexFlow?",
                answer:
                    "Insurance companies validate and approve invoices submitted on the platform, ensuring that only legitimate and verified claims are financed.",
            },
            {
                question: "12. Does InvexFlow impact existing claim processes?",
                answer:
                    "No. InvexFlow works alongside existing claim workflows and does not alter claim approval or settlement timelines.",
            },
            {
                question: "13. How are payments handled?",
                answer:
                    "Once an invoice is financed, the insurance company settles the claim directly with the designated financing bank instead of the service provider.",
            },
        ],
    },

    {
        category: "For Banks & Financial Institutions",
        items: [
            {
                question: "14. How do banks participate in InvexFlow?",
                answer:
                    "Banks act as financing partners by providing funds against approved invoices. InvexFlow facilitates verified invoice sourcing, risk-filtered opportunities, and digital workflow integration.",
            },
            {
                question: "15. How is risk managed?",
                answer:
                    "Risk mitigation includes insurance-backed invoice validation, pre-approved claims, structured transaction flows, and transparent documentation with audit trails.",
            },
            {
                question: "16. What are the benefits for banks?",
                answer:
                    "Banks gain access to short-term, asset-backed financing opportunities, diversified exposure across industries, and digitized onboarding and monitoring.",
            },
        ],
    },

    {
        category: "For Investors",
        items: [
            {
                question: "17. What investment opportunities does InvexFlow offer?",
                answer:
                    "InvexFlow enables participation in short-term, invoice-backed financing through structured arrangements with financial institutions or pooled investment models.",
            },
            {
                question: "18. What is the typical investment tenure?",
                answer:
                    "Investment cycles generally range from 30 to 60 days depending on invoice maturity.",
            },
            {
                question: "19. How are returns generated?",
                answer:
                    "Returns are generated through invoice discounting margins earned during the financing cycle.",
            },
            {
                question: "20. What are the key risks?",
                answer:
                    "Potential risks include delays in insurance claim settlement and counterparty risk. These are mitigated through validation, structured flows, and partnerships with regulated financial institutions.",
            },
        ],
    },

    {
        category: "Security & Compliance",
        items: [
            {
                question: "21. Is my data secure?",
                answer:
                    "Yes. InvexFlow uses advanced encryption and secure infrastructure to protect all data and transactions.",
            },
            {
                question: "22. Is InvexFlow compliant with regulations?",
                answer:
                    "InvexFlow operates in alignment with applicable financial regulations and works with regulated banks and financial institutions.",
            },
            {
                question: "23. How are funds transferred?",
                answer:
                    "All funds are transferred through regulated banking channels, ensuring transparency and auditability.",
            },
            {
                question: "24. Can I track my transactions?",
                answer:
                    "Yes. The platform provides real-time tracking of invoices, approvals, funding, and settlements.",
            },
        ],
    },

    {
        category: "Onboarding & Support",
        items: [
            {
                question: "25. How do I get started?",
                answer:
                    "You can register on the platform and complete the onboarding process, including KYC and documentation submission.",
            },
            {
                question: "26. What documents are required?",
                answer:
                    "Typical requirements include business registration documents, bank account details, insurance claim documentation, and identification with compliance information.",
            },
            {
                question: "27. Is there a minimum volume requirement?",
                answer:
                    "Minimum requirements may apply depending on the financing partner and business profile.",
            },
            {
                question: "28. Do you provide customer support?",
                answer:
                    "Yes. Our support team is available to assist with onboarding, transactions, and platform usage.",
            },
        ],
    },
];

const FAQPage = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    let faqCounter = 0;

    return (
        <section className="bg-[#D9F4EF] py-16 md:py-24 px-4">
            <div className="container mx-auto max-w-5xl">

                {/* Header */}
                <div className="mb-12">
                    <span className="inline-block mb-4 px-4 py-1 text-sm rounded-full bg-white text-gray-600">
                        Support & Information
                    </span>

                    <h1 className="text-[38px] md:text-[52px] leading-[1.15] font-semibold text-[#111827] mb-5">
                        Frequently Asked Questions
                    </h1>

                    <p className="text-gray-600 text-[16px] md:text-[18px] leading-[30px] max-w-3xl">
                        Find answers to the most common questions about
                        InvexFlow, invoice financing, onboarding, security,
                        and platform operations.
                    </p>
                </div>

                {/* FAQ Sections */}
                <div className="space-y-10">
                    {faqData.map((section, sectionIndex) => (
                        <div
                            key={sectionIndex}
                            className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-6 md:p-8"
                        >
                            <h2 className="text-2xl font-semibold text-[#111827] mb-6">
                                {section.category}
                            </h2>

                            <div className="space-y-4">
                                {section.items.map((item, itemIndex) => {
                                    faqCounter++;
                                    const currentIndex = faqCounter;

                                    return (
                                        <div
                                            key={itemIndex}
                                            className="border border-[#E2E8F0] rounded-xl overflow-hidden transition-all duration-300"
                                        >
                                            {/* Question */}
                                            <button
                                                onClick={() =>
                                                    toggleAccordion(currentIndex)
                                                }
                                                className="w-full flex items-center justify-between gap-4 text-left p-5 hover:bg-[#F8FAFC] transition"
                                            >
                                                <span className="text-[#111827] font-medium text-[16px] md:text-[17px]">
                                                    {item.question}
                                                </span>

                                                <ChevronDown
                                                    className={`min-w-[20px] transition-transform duration-300 ${openIndex === currentIndex
                                                            ? "rotate-180 text-[#0FB9B1]"
                                                            : "rotate-0 text-[#64748B]"
                                                        }`}
                                                />
                                            </button>

                                            {/* Answer */}
                                            <div
                                                className={`grid transition-all duration-300 ease-in-out ${openIndex === currentIndex
                                                        ? "grid-rows-[1fr]"
                                                        : "grid-rows-[0fr]"
                                                    }`}
                                            >
                                                <div className="overflow-hidden">
                                                    <div className="px-5 pb-5 text-gray-600 leading-[30px] text-[15px]">
                                                        {item.answer}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Contact Card */}
                <div className="mt-12 bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-8 text-center">
                    <h3 className="text-2xl font-semibold text-[#111827] mb-3">
                        Still Have Questions?
                    </h3>

                    <p className="text-gray-600 leading-[30px] mb-5">
                        If you need more information, feel free to contact us.
                    </p>

                    <a
                        href="mailto:info@invexflow.com"
                        className="inline-flex items-center justify-center bg-[#0FB9B1] hover:bg-[#0da39c] transition text-white px-6 py-3 rounded-lg font-medium"
                    >
                        info@invexflow.com
                    </a>
                </div>

            </div>
        </section>
    );
};

export default FAQPage;